import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { ExtraProps } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeSanitize from 'rehype-sanitize';
import rehypeExternalLinks from 'rehype-external-links';
import { visit } from 'unist-util-visit';
import type { Root } from 'mdast';
import { cn } from '@lib/utils';
import { richTextSanitizeSchema, richTextLongFormSanitizeSchema } from '@utils/sanitizeConfig';
import { remarkLinkifyBareUrls, remarkNormalizeLinkProtocols } from '@utils/linkifyText';
import { stripMarkdown } from '@utils/stripMarkdown';

const LINE_CLAMP: Record<number, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
};

const CELL_ALIGN: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

/**
 * GFM column alignment as a class.
 *
 * Read off the hast node rather than the props: the sanitizer keeps alignment
 * as `align="right"` (an attribute its default schema already allows), but
 * react-markdown rewrites that into an inline `text-align` style before a
 * component sees it. Spending it as a class keeps `style` out of the rendered
 * markup and out of the schema, where it must stay — inline style on
 * author-supplied elements is a UI-redress surface.
 */
function cellAlignClass(node: ExtraProps['node']): string | undefined {
  const align = node?.properties.align;
  return typeof align === 'string' ? CELL_ALIGN[align] : undefined;
}

/**
 * remark plugin: turns a heading into an ordinary paragraph.
 *
 * Used on surfaces whose schema has no heading tags, and it has to happen
 * before the sanitizer: unwrapping an `<h2>` leaves the words behind as bare
 * text and the block boundary goes with the tag, so `## A` and `## B` paint as
 * one run-on line. Demoting first keeps each heading in a block of its own and
 * leaves the tag whitelist as the backstop it is meant to be.
 */
function remarkHeadingsAsParagraphs() {
  return (tree: Root): undefined => {
    visit(tree, 'heading', (node, index, parent) => {
      if (!parent || index === undefined) return;
      parent.children.splice(index, 1, { type: 'paragraph', children: node.children });
    });
  };
}

export interface RichTextDisplayProps {
  content: string | null | undefined;
  className?: string;
  linkClassName?: string;
  /** Collapse newlines to single spaces (for compact feed views) */
  collapseNewlines?: boolean;
  /** Maximum number of lines to display (adds ellipsis if truncated) */
  maxLines?: number;
  /**
   * Disable markdown rendering — show the words with the syntax stripped
   * (prevents formatting-based clickbait, and leaves URLs unclickable so a
   * tap reaches the card underneath). Used by feed/profile previews.
   */
  plainTextOnly?: boolean;
  /** Allow h1–h6 headings. Reserved for long-form, owner-authored surfaces (e.g. Space "About"). */
  allowHeadings?: boolean;
}

/**
 * RichTextDisplay - sanitized markdown rendering (inline marks plus lists,
 * blockquote, hr, code blocks, and GFM tables; headings opt-in via
 * `allowHeadings`).
 *
 * AIDEV-NOTE: renders bold/italic/strike/code/links/lists/blockquote/hr/code-blocks/tables; headings opt-in via allowHeadings
 *
 * Line breaks: remark-breaks turns a single newline into a <br>, and blank
 * lines stay paragraph breaks. Do NOT reintroduce `whitespace-pre-wrap` on the
 * markdown wrapper to get the same effect — markdown-to-HTML inserts its own
 * structural newlines between blocks, so the CSS paints those too: every
 * paragraph gap doubles and list text drops to the line below its bullet.
 * Whitespace collapsing being ON is what keeps those newlines invisible, so
 * every block the sanitizer would unwrap has to keep a block of its own
 * instead — hence the table tags in the schema and `remarkHeadingsAsParagraphs`
 * above.
 *
 * Security:
 * - rehype-sanitize blocks XSS attacks
 * - Protocol whitelist blocks javascript:, data:, vbscript:
 * - All links open in new tab with rel="noopener noreferrer"
 */
const RichTextDisplay: React.FC<RichTextDisplayProps> = ({
  content,
  className,
  linkClassName = 'text-blue-600 hover:text-blue-800 underline',
  collapseNewlines = false,
  maxLines,
  plainTextOnly = false,
  allowHeadings = false,
}) => {
  if (!content) {
    return null;
  }

  let processedContent = content;

  // Order matters: strip syntax BEFORE collapsing newlines. The block-marker
  // rules (bullets, headings, quotes) are line-anchored, so a collapse first
  // would leave every "- " and "# " stranded mid-line.
  if (plainTextOnly) {
    processedContent = stripMarkdown(processedContent);
  }

  if (collapseNewlines) {
    processedContent = processedContent.replace(/\n+/g, ' ');
  }

  // Spelled out rather than interpolated: Tailwind v4 extracts literal class
  // candidates from source, so `line-clamp-${maxLines}` would emit no CSS and
  // the clamp would silently do nothing.
  const lineClampClass = maxLines ? LINE_CLAMP[maxLines] : undefined;

  // Plain text mode: skip markdown rendering entirely. The syntax is removed
  // rather than printed — a preview that shows literal `**bold**` is worse
  // than either rendering it or hiding it.
  if (plainTextOnly) {
    return (
      <p
        dir="auto"
        className={cn(
          'wrap-break-word',
          !collapseNewlines && 'whitespace-pre-wrap',
          lineClampClass && 'overflow-hidden',
          lineClampClass,
          className,
        )}
      >
        {processedContent}
      </p>
    );
  }

  return (
    <div
      dir="auto"
      className={cn(
        'wrap-break-word',
        lineClampClass && 'overflow-hidden',
        lineClampClass,
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm,
          remarkBreaks,
          remarkLinkifyBareUrls,
          remarkNormalizeLinkProtocols,
          ...(allowHeadings ? [] : [remarkHeadingsAsParagraphs]),
        ]}
        rehypePlugins={[
          [rehypeSanitize, allowHeadings ? richTextLongFormSanitizeSchema : richTextSanitizeSchema],
          [rehypeExternalLinks, { target: '_blank', rel: ['noopener', 'noreferrer'] }],
        ]}
        components={{
          p: ({ children }) => <p className="leading-relaxed mb-2 last:mb-0">{children}</p>,
          strong: ({ children }) => <strong className="font-bold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          del: ({ children }) => <del className="line-through">{children}</del>,
          // Inline code keeps the pill; fenced blocks render plain (the <pre>
          // below owns the block styling). react-markdown v9 dropped the
          // `inline` prop, so detect a block via the language-* class or a
          // newline in the content (fenced content always carries one).
          code: ({ className, children }) => {
            // A fenced block has a `language-*` class or (for no-language
            // fences) multiline string content; CommonMark fences are always
            // multiline, so inline code never trips the newline check.
            const hasLanguage = (className ?? '').includes('language-');
            const isMultiline = typeof children === 'string' && children.includes('\n');
            const isBlock = hasLanguage || isMultiline;
            return isBlock ? (
              <code className="font-mono text-sm">{children}</code>
            ) : (
              <code className="bg-gray-100 px-1.5 py-0.5 rounded-sm text-sm font-mono">
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className="bg-gray-100 rounded-md p-3 my-2 overflow-x-auto text-sm">
              {children}
            </pre>
          ),
          ul: ({ children }) => <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-gray-300 pl-3 my-2 italic text-gray-700">
              {children}
            </blockquote>
          ),
          hr: () => <hr className="my-3 border-gray-200" />,
          // The wrapper is the scroll container, not the page: a table with
          // more columns than a phone is wide has to scroll inside the post
          // rather than drag the whole feed sideways. `w-full` keeps a short
          // schedule looking deliberate; anything whose columns cannot fit
          // overflows the wrapper and scrolls.
          table: ({ children }) => (
            <div className="my-2 overflow-x-auto">
              <table className="w-full border-collapse text-sm">{children}</table>
            </div>
          ),
          // `text-left` overrides the UA default of centred header cells, so an
          // unaligned column reads the same in the head as in the body.
          th: ({ children, node }) => (
            <th
              className={cn(
                'border border-gray-200 bg-gray-100 px-3 py-1.5 text-left font-semibold',
                cellAlignClass(node),
              )}
            >
              {children}
            </th>
          ),
          td: ({ children, node }) => (
            <td className={cn('border border-gray-200 px-3 py-1.5', cellAlignClass(node))}>
              {children}
            </td>
          ),
          // Headings are down-shifted by one level: markdown `#` keeps the
          // biggest visual style but renders as <h2>, so owner-authored About
          // copy never emits a second page <h1> or skips levels. Elements clamp
          // at <h6>; visual sizes stay tied to the markdown level.
          // `className` is forwarded, not replaced: the sanitizer deliberately
          // preserves `sr-only` on the heading GFM footnotes generate, and
          // dropping it turns a screen-reader label into a visible "Footnotes"
          // banner in the middle of owner-authored copy.
          h1: ({ children, className: c }) => (
            <h2 className={cn('text-2xl font-bold mt-3 mb-2', c)}>{children}</h2>
          ),
          h2: ({ children, className: c }) => (
            <h3 className={cn('text-xl font-bold mt-3 mb-2', c)}>{children}</h3>
          ),
          h3: ({ children, className: c }) => (
            <h4 className={cn('text-lg font-semibold mt-2 mb-1', c)}>{children}</h4>
          ),
          h4: ({ children, className: c }) => (
            <h5 className={cn('text-base font-semibold mt-2 mb-1', c)}>{children}</h5>
          ),
          h5: ({ children, className: c }) => (
            <h6 className={cn('text-sm font-semibold mt-2 mb-1', c)}>{children}</h6>
          ),
          h6: ({ children, className: c }) => (
            <h6 className={cn('text-sm font-semibold mt-2 mb-1', c)}>{children}</h6>
          ),
          // A destination the sanitizer stripped (unsupported protocol) or
          // `normalizeLinkUrl` declined arrives with no href. Styling it anyway
          // produced a blue, underlined thing that did nothing on click — worse
          // than plain text, because it also disguises a rejected `javascript:`
          // payload as an ordinary link.
          // `node` is react-markdown's hast node, not an attribute: destructure
          // it out or the spread puts `node="[object Object]"` on every link.
          a: ({ children, node: _node, ...props }) =>
            props.href ? (
              <a
                {...props}
                className={linkClassName}
                onClick={e => {
                  e.stopPropagation();
                }}
              >
                {children}
              </a>
            ) : (
              children
            ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default React.memo(RichTextDisplay);
